import { Capacitor } from '@capacitor/core';
import { Keyboard } from '@capacitor/keyboard';
import { MutableRefObject, useEffect } from 'react';

/**
 * Custom hook to handle scrolling to the active element when the keyboard appears.
 * This is necessary on iOS because the keyboard often hides form elements
 * when the user taps on a form field.
 * The hook ensures the active element is scrolled into view to improve usability.
 * Pass a ref to the scrollable container element where the form fields are rendered.
 */
export const useIosKeyboardScroller = (
  scrollRef: MutableRefObject<HTMLDivElement | null>,
): void => {
  useEffect(() => {
    const platform = Capacitor.getPlatform();

    if (platform !== 'ios') {
      return () => {};
    }

    const listener = Keyboard.addListener('keyboardDidShow', (info) => {
      if (!document.activeElement) {
        scrollRef.current?.scrollTo({
          top: info.keyboardHeight,
          behavior: 'smooth',
        });
        return;
      }

      const elementRect = document.activeElement.getBoundingClientRect();
      // window.innerHeight doesn't update synchronously on iOS when the keyboard appears,
      // so we calculate the actual visible viewport by subtracting the keyboard height.
      const availableHeight = window.innerHeight - info.keyboardHeight;

      if (elementRect.y > availableHeight) {
        scrollRef.current?.scrollBy({
          top: Math.round(
            // Adding a small extra offset (16px) ensures the element is fully visible within the viewport.
            // This accounts for any minor discrepancies in positioning or layout adjustments.
            elementRect.y - availableHeight + elementRect.height + 16,
          ),
          behavior: 'smooth',
        });
      }
    });

    // Remove only our listener.
    return () => {
      listener.then((handle) => handle.remove());
    };
  }, [scrollRef]);
};
